// SEOHead — a single primitive every public page uses to declare its title,
// description, canonical URL, social cards, and any JSON-LD structured data.
//
// Why a wrapper around react-helmet-async (instead of letting each page roll
// its own Helmet block)?
//
//  1. Defaults. Most pages share the same OG image, twitter:card type, and
//     canonical-URL prefix. A primitive lets us set those once and override
//     only what's different.
//  2. Consistency. og:title / twitter:title / <title> drift if each page
//     wires them independently. Here, one prop fans out to all three.
//  3. JSON-LD. Pages frequently want to declare more than one schema (FAQ +
//     Organization + Breadcrumb). The `jsonLd` prop accepts either a single
//     object or an array, and the wrapper emits a <script> tag per entry.
//  4. noIndex. Some pages (404, 500) should never be indexed. A boolean prop
//     handles that without each page hand-rolling a meta-robots tag.
//
// NOTE on SSR: this is fully client-side (helmet hydrates on mount). Modern
// crawlers (Googlebot, Bingbot, Facebook 2024+, Twitter 2024+) render JS and
// will see these tags fine. Strict legacy crawlers might miss them — if that
// becomes a problem we'll add a Vite-SSG pre-render step in v1.1.

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { DEFAULT_OG_IMAGE, SITE_NAME, SITE_URL } from './seo-data';

export interface SEOProps {
  /** Full <title>. Should already include the brand suffix (e.g. "Help | RealtyIQ.io"). */
  title: string;
  /** Meta description. Aim for 150-160 chars; Google truncates beyond ~160. */
  description: string;
  /**
   * Override for og:description / twitter:description only. When omitted,
   * `description` is used for both. Useful for property pages where the social
   * card copy differs from the meta description.
   */
  ogDescription?: string;
  /** Canonical URL. Defaults to https://realtyiq.io + current pathname. */
  canonical?: string;
  /** Absolute URL to the social-card image. Defaults to /og-image.png. */
  ogImage?: string;
  /** Width of the og:image in pixels (for large property photos use 1200). */
  ogImageWidth?: number;
  /** Height of the og:image in pixels (for large property photos use 630). */
  ogImageHeight?: number;
  /** Schema.org og:type. Defaults to 'website'. Use 'article' for blog posts. */
  ogType?: string;
  /** Set true on 404 / 500 / dev-only pages we never want indexed. */
  noIndex?: boolean;
  /**
   * JSON-LD structured data. Pass a single Schema.org object or an array of
   * them — each becomes its own <script type="application/ld+json"> block.
   */
  jsonLd?: object | object[];
}

function resolveCanonical(canonical: string | undefined): string {
  if (canonical) return canonical;
  if (typeof window !== 'undefined' && window.location?.pathname) {
    // Strip trailing slash on non-root paths so /help and /help/ resolve to
    // the same canonical (helps avoid duplicate-content signals).
    const path = window.location.pathname;
    const normalized =
      path !== '/' && path.endsWith('/') ? path.slice(0, -1) : path;
    return `${SITE_URL}${normalized}`;
  }
  return SITE_URL;
}

export function SEOHead({
  title,
  description,
  ogDescription,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogImageWidth,
  ogImageHeight,
  ogType = 'website',
  noIndex = false,
  jsonLd,
}: SEOProps): React.ReactElement {
  const resolvedCanonical = resolveCanonical(canonical);
  const socialDescription = ogDescription ?? description;
  const jsonLdEntries = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <Helmet>
      {/* Core */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={resolvedCanonical} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={resolvedCanonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={socialDescription} />
      <meta property="og:image" content={ogImage} />
      {ogImageWidth != null && <meta property="og:image:width" content={String(ogImageWidth)} />}
      {ogImageHeight != null && <meta property="og:image:height" content={String(ogImageHeight)} />}
      <meta property="og:image:alt" content={title} />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={socialDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={title} />

      {/* JSON-LD — Helmet only deduplicates by tag attributes, so we key each
          script by its position in the array. */}
      {jsonLdEntries.map((entry, idx) => (
        <script
          key={`jsonld-${idx}`}
          type="application/ld+json"
        >{JSON.stringify(entry)}</script>
      ))}
    </Helmet>
  );
}
